import { createRouter, createWebHistory } from 'vue-router'

import MainLayout from '../views/layouts/main-layout/MainLayout'
import { setLastTab } from '@/router/utils/lastTabStorage'
// The side menu definition is read from the module itself, not the store:
// store/modules/auth imports the router, importing the store here would be a cycle.
import sideMenuModule from '@/store/modules/side-menu/index'
import {
  buildMenuIndex,
  isMeasurableNavigation,
  resolvePageContext
} from '@/observability/faroContext'
import { trackPageView, trackRouteLoaded } from '@/observability/faro'
import {
  ADDRESS_BOOK_LAST_TAB_KEY,
  ADDRESS_BOOK_TAB_ROUTE_NAMES
} from '@/router/contacts/addressBookTabs'

import { dossierPages } from './dossiers'
import { loginRootPages } from './login/index'
import { screenRootPages } from './screen/index'
import { calendarPages } from './calendar'
import { invoicingPages } from './invoicing'
import { noteRootPages } from '@/router/note'
import { settingsSubPages } from './settings/index'
import { saManagementSubPages } from './sa_management/index'
import { profileRootPages } from './profile/index'

import { contactsRootPages, contactsSubPages } from './contacts/index'
import { wikiRootPages, wikiSubPages } from './wiki/index'
import { tasksSubPages } from './tasks/index'
import { partnersRootPages } from './partners/index'
import { statisticsRootPages, statisticsSubPages } from '@/router/statistics'
import { chatRootPages } from '@/router/chat'
import { supplierOrderSubPages } from '@/router/supplierOrder'
import { versionNoteRootPages } from '@/router/versionNote'
import { templateDashboardSubPages } from '@/router/templateDashboard'
import { modelNotificationRootPages } from '@/router/modelNotification'
import { electronicSignatureRootPages } from '@/router/electronicSignature'
import { stockSubPages } from '@/router/stock'
import { catalogRootPages } from '@/router/catalog/catalog'
import { marketplacePages } from '@/router/marketplace'
import { analyticsPages } from '@/router/analytics'
import { personRootPages } from '@/router/person'
import { supplierRootPages } from '@/router/supplier'
import { itemRootPages } from '@/router/item'
import { userRootPages } from '@/router/user'
import { condolenceRootPages } from '@/router/condolence'
import { activityLogsPages } from '@/router/activityLog'
import { exportSubPages } from '@/router/export'
import { scheduledTaskSubPages } from '@/router/scheduledTask'
import { designSystemPages } from '@/router/designSystem'
import { reportRootPages } from '@/router/reports'

const routes = [
  ...loginRootPages,
  ...screenRootPages,
  {
    path: '/',
    name: 'root',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'root_base',
        //rendre ici la redirection dynamique.. store.getters['auth/getAuth']['settings'][landing_page]]
        redirect: { name: 'dashboard_dossier_main_page_route' }
      },
      { ...contactsRootPages },
      { ...noteRootPages },
      { ...partnersRootPages },
      { ...noteRootPages },
      { ...profileRootPages },
      { ...statisticsRootPages },
      { ...wikiRootPages },
      ...analyticsPages,
      ...marketplacePages,
      ...personRootPages,
      ...supplierRootPages,
      ...itemRootPages,
      ...userRootPages,
      ...calendarPages,
      ...chatRootPages,
      ...contactsSubPages,
      ...dossierPages,
      ...invoicingPages,
      ...modelNotificationRootPages,
      ...saManagementSubPages,
      ...settingsSubPages,
      ...statisticsSubPages,
      ...supplierOrderSubPages,
      ...tasksSubPages,
      ...reportRootPages,
      ...templateDashboardSubPages,
      ...chatRootPages,
      ...versionNoteRootPages,
      ...catalogRootPages,
      ...wikiSubPages,
      ...electronicSignatureRootPages,
      ...stockSubPages,
      ...condolenceRootPages,
      ...activityLogsPages,
      ...exportSubPages,
      ...scheduledTaskSubPages,
      ...designSystemPages
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash
      }
    }
    if (savedPosition) {
      return savedPosition
    }
    return { left: 0, top: 0 }
  }
})

let menuIndex = null
const resolveMenuPath = (name) => {
  try {
    return router.resolve({ name }).path
  } catch {
    // Menu entry whose route needs params (or no longer exists): no path.
    return null
  }
}

let navigationStartedAt = 0
let visibilityAtNavigationStart = 'visible'
router.beforeEach(() => {
  navigationStartedAt = performance.now()
  visibilityAtNavigationStart = document.visibilityState
})

router.afterEach((to, from, failure) => {
  if (failure) {
    return
  }
  menuIndex ??= buildMenuIndex(sideMenuModule.state().menu, resolveMenuPath)
  const page = resolvePageContext(to, menuIndex)
  trackPageView(page)
  // Measured until the next frame after the route components are rendered:
  // the before/after metric for the lazy-loading refactor.
  requestAnimationFrame(() => {
    if (
      isMeasurableNavigation(
        visibilityAtNavigationStart,
        document.visibilityState
      )
    ) {
      trackRouteLoaded(page, performance.now() - navigationStartedAt)
    }
  })
})

router.afterEach((to) => {
  if (to.name === 'dashboard_page_route') {
    setLastTab('dashboard:last_tab_id', to.params.id)
  } else if (to.name === 'dashboard_dossier_page_route') {
    setLastTab('dashboard_dossier:last_tab_id', to.params.id)
  } else if (
    to.meta?.isDossierPage &&
    (!to.meta?.display_type || to.meta.display_type === 'view') &&
    Object.keys(to.params).length === 1
  ) {
    // Only cache tabs reachable with the dossier id alone — some subpages
    // (document preview, wiki documents...) require extra params we don't have on redirect.
    setLastTab('dossier:last_subpage_route_name', to.name)
  } else if (ADDRESS_BOOK_TAB_ROUTE_NAMES.includes(to.name)) {
    // The four address book tabs take no params, so the route name alone is
    // enough to navigate back to one.
    setLastTab(ADDRESS_BOOK_LAST_TAB_KEY, to.name)
  }
})

export default router
